import { useEffect, useState, type ReactNode } from 'react';
import { PanelRightOpen } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui-kit/sheet';
import { useIsMobile } from '@/hooks/use-media-query';
import { useDocxBuilder } from './context';
import { DocxBuilderHeader } from './header';
import { DocxBuilderStepUpload } from './step-upload';
import { DocxBuilderStepSelection } from './step-selection';
import { DocxBuilderStepPublish } from './step-publish';
import { DocxBuilderPanelDetails } from './panel-details';
import { DocxBuilderPanelPublish } from './panel-publish';
import DocxBuilderPanelSelection from './panel-selection';
import { DocxSelectionProvider, useDocxSelection } from './selection/context';

function StepCard({
  stepNumber,
  title,
  description,
  children,
}: {
  stepNumber: number;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-[hsl(var(--fg))]">
          {stepNumber}. {title}
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
      </div>
      {children}
    </div>
  );
}

function Canvas() {
  const { step } = useDocxBuilder();

  if (step === 1) {
    return (
      <div className="p-6">
        <StepCard
          stepNumber={1}
          title="Upload your document"
          description="Upload a completed example — with real names, dates and amounts filled in — so you can mark the parts that change."
        >
          <DocxBuilderStepUpload />
        </StepCard>
      </div>
    );
  }

  // Step 2 fills the whole canvas (no card chrome) — DocxCanvas manages its own scroll.
  if (step === 2) {
    return (
      <div className="h-full p-4">
        <DocxBuilderStepSelection />
      </div>
    );
  }

  return (
    <div className="p-6">
      <StepCard
        stepNumber={3}
        title="Publish"
        description="Make the template available to your organization."
      >
        <DocxBuilderStepPublish />
      </StepCard>
    </div>
  );
}

function RightPanel() {
  const { step } = useDocxBuilder();

  // Step 2's panel manages its own padding/borders + internal scroll (list + editor chrome).
  if (step === 2) return <DocxBuilderPanelSelection />;

  return (
    <div className="h-full overflow-y-auto p-4">
      {step === 1 ? <DocxBuilderPanelDetails /> : <DocxBuilderPanelPublish />}
    </div>
  );
}

// Contextual label/trigger copy for the mobile right-panel drawer, per step.
const PANEL_LABEL: Record<number, string> = { 1: 'Details', 2: 'Fields', 3: 'Publish' };

export function DocxBuilderLayout() {
  return (
    <DocxSelectionProvider>
      <BuilderBody />
    </DocxSelectionProvider>
  );
}

function BuilderBody() {
  const { step } = useDocxBuilder();
  const { draft, savedCount } = useDocxSelection();
  const isMobile = useIsMobile();
  const [panelOpen, setPanelOpen] = useState(false);

  // On mobile the right panel becomes a drawer, so opening a field draft
  // (marking new text or editing an existing field) must reveal it — that's
  // where the editor lives.
  useEffect(() => {
    if (isMobile && draft) setPanelOpen(true);
  }, [isMobile, draft]);

  // Changing steps swaps the panel's contents entirely; close the drawer so the
  // next step starts from the canvas rather than a stale open panel.
  useEffect(() => {
    setPanelOpen(false);
  }, [step]);

  return (
    <div className="flex flex-col h-full">
      {/* Compact toolbar: back, title, badge, stepper */}
      <DocxBuilderHeader />

      {/* Content — fills remaining height. On md+ the canvas and right panel sit
          side by side, each scrolling independently; on mobile the canvas is
          full-width and the panel moves into the drawer below. */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 min-w-0 overflow-y-auto">
          <Canvas />
        </div>

        {!isMobile && (
          <div className="w-100 2xl:w-120 shrink-0 overflow-hidden border-l border-border">
            <RightPanel />
          </div>
        )}
      </div>

      {/* Mobile: the right panel as a drawer, opened by a floating button. */}
      {isMobile && (
        <>
          <Button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="fixed bottom-4 right-4 z-40 gap-2 rounded-full shadow-lg"
            aria-label={`Open ${PANEL_LABEL[step]} panel`}
          >
            <PanelRightOpen className="h-4 w-4" />
            {PANEL_LABEL[step]}
            {step === 2 && savedCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/25 px-1.5 text-xs font-semibold">
                {savedCount}
              </span>
            )}
          </Button>

          <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
            <SheetContent side="right" className="w-[88vw] max-w-md p-0 flex flex-col">
              <SheetTitle className="sr-only">{PANEL_LABEL[step]}</SheetTitle>
              <div className="flex-1 min-h-0 overflow-hidden">
                <RightPanel />
              </div>
            </SheetContent>
          </Sheet>
        </>
      )}
    </div>
  );
}
